import { Box, Grid, InputAdornment } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/**API */
import ClinicApi from 'api/master/clinic.api'
import ReportApi, { FindAllReportInterface } from 'api/report.api'
import { getBranch, toBuddhistYear } from 'utils/app.utils'

/**COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import { useDownloadExcel } from 'component/Excel/hooks/useExcel'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import Loading from 'component/Loading'
import AppointmentList from './AppointmentList'

/**STYLE */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as UseStyled from 'features/report/useStyled'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import SelectCheckBoxInput from 'new-components/inputs/select_checkbox_input'
import TextInput from 'new-components/inputs/text-input'

type Doctors = {
  userId: number
  firstname: string
  lastname: string
  fullname: string
}

export interface DentistInterface {
  dentistId: number
  dentistFullname: string
}

export default function Appointment() {
  const { t } = useTranslation()
  const history = useHistory()
  const componentRef = useRef<HTMLDivElement>(null)
  const momentNow = moment().format('YYYY-MM-DD')
  const momentNowRange = [moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
  const momentDate = () => momentNowRange
  const [branchId, setBranchId] = useState<any>(getBranch())
  const [branches, setBranches] = useState<any[]>([])
  const [rowCount, setRowCount] = useState<number>(0)
  const [sortBy, setSortBy] = useState<string>('createAt')
  const [sortType, setSortType] = useState<string>('ASC')
  const [doctors, setDoctors] = useState<Doctors[]>([])
  const [date, setDate] = useState<string>(momentNow)
  const [page, setPage] = useState<number>(1)
  const [search, setSearch] = useState<string>('')
  const [pageLimit, setPageLimit] = useState<number>(10)
  const [rangeDate, setRangeDate] = useState<string[]>(momentDate)
  const [fetch, setFetch] = useState<boolean>(false)

  const [loading, setLoading] = useState<boolean>(true)
  const [selectDoctors, setSelectDoctors] = useState<any[]>([])

  const [doctorsMultiple, setDoctorsMultiple] = useState<DentistInterface[]>([])
  const [openFilterDoctors, setOpenFilterDoctors] = useState(false)

  const [appointments, setAppointments] = useState<[]>([])
  const [exportAppointments, setExportAppointments] = useState<[]>([])

  const handleFilterDoctors = (value: any) => {
    if ((value === 'ALL' && _.size(selectDoctors.filter((item) => typeof item === 'number')) === _.size(doctors)) || value === 'CLEAR') setSelectDoctors([''])
    else if (value === 'ALL') setSelectDoctors(['ALL', ..._.map(doctors, (d: any) => d.userId)])
    else if (_.includes(selectDoctors, value)) setSelectDoctors(_.filter(selectDoctors, (d) => d !== value && d !== 'ALL'))
    else setSelectDoctors([...selectDoctors, value])
  }

  const getAllFilter = () => {
    return [
      {
        label: t('REPORT.FILTER.BRANCH'),
        value: _.get(_.find(branches, { branchId }), 'branchName') || _.get(_.find(branches, { branchId }), 'branchNameEn')
      },
      {
        label: t('REPORT.FILTER.DOCTOR'),
        value: getFilterUser()
      },
      {
        label: t('REPORT.FILTER.DATE_START'),
        value: rangeDate[0] ? toBuddhistYear(moment(rangeDate[0]), 'DD/MM/YYYY') : '-'
      },
      {
        label: t('REPORT.FILTER.DATE_END'),
        value: rangeDate[1] ? toBuddhistYear(moment(rangeDate[1]), 'DD/MM/YYYY') : '-'
      }
    ]
  }

  const getFilterUser = () => {
    if (_.includes(selectDoctors, 'ALL')) return ` ${t('REPORT.REPORT_GROUP.ALL')}`
    else {
      return ` ${doctorsMultiple
        .filter((d: DentistInterface) => selectDoctors.includes(d.dentistId))
        ?.map((d: DentistInterface) => d.dentistFullname)
        .join(', ')}`
    }
  }

  const verifyPermission = async () => {
    const condition: FindAllReportInterface = {
      page: 1,
      pageLimit: 200
    }
    // ReportApi.reportList(condition).then(({ data }) => {
    //   const check = _.find(data, (val: any) => String(val.reportKey) === 'DF_LAB') ? true : false
    //   if (!check) {
    //     history.push(routeName.report)
    //     return false
    //   }
    // })

    ClinicApi.findAllBranches()
      .then(({ data }) => setBranches(data))
      .catch((e) => {
        return
      })
    ClinicApi.findAllDoctors('', '', rangeDate[0], rangeDate[1])
      .then((resp) => {
        setDoctors([...resp.data])
        setSelectDoctors(['ALL', ...resp.data.map((d: any) => d.userId)])
      })
      .catch((e) => {
        return
      })

    // DoctorScheduleApi.findAllDoctor({ branchId: branchId }).then(({ data: dataAllDoctor }) => {
    //     const dentists: DentistInterface[] = dataAllDoctor.map((d: any) => {
    //         return {
    //             dentistId: d.userId,
    //             dentistFullname: d.fullname
    //         }
    //     })
    //     setDoctorsMultiple(dentists)
    //     setSelectDoctors(['ALL', ...dentists.map((d: DentistInterface) => d.dentistId)])
    // })

    return true
  }

  useEffect(() => {
    verifyPermission()
  }, [branchId])

  const fetchAppointment = useCallback(async () => {
    let condition: any = {
      page: page,
      pageLimit: pageLimit,
      branchId: branchId || null,
      dateStart: rangeDate[0],
      dateEnd: rangeDate[1]
    }
    if (!_.isEmpty(search)) {
      condition.search = search
    }
    if (doctors) {
      const doctor = _.filter(selectDoctors, (v: any) => v > 0).join(',')
      if (doctor) condition = { ...condition, doctorId: _.filter(selectDoctors, (v: any) => v > 0).join(',') }
    }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }

    ReportApi.reportAppointment(condition)
      .then(({ headers, data }) => {
        setRowCount(headers['x-total'])
        setAppointments(data.rows)
        setExportAppointments(data.rowsExport)
        setFetch(false)
      })
      .catch((e) => {
        setLoading(false)
        setFetch(false)
      })
      .finally(() => {
        setLoading(false)
        setFetch(false)
      })
  }, [page, pageLimit, sortBy, sortType, fetch])

  const { onDownload } = useDownloadExcel({
    tableExportRef: componentRef.current,
    filename: `${t(`REPORT.REPORT_NAME.APPOINTMENT`)} (${date})`,
    sheetName: `${t(`REPORT.REPORT_NAME.APPOINTMENT`)}`
  })

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangeRowsPerPage = (limit: number) => setPageLimit(limit)

  const handleChangePage = (val: number) => setPage(val)

  useEffect(() => {
    fetchAppointment()
  }, [fetchAppointment])


  return (
    <Box sx={{ height: '100%' }}>
      <HeaderCard text={t('REPORT.REPORT_NAME.APPOINTMENTS')} />
      <Box sx={{ px: 3, pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5} md={3} xl={2}>
            <TextInput
              label={t('REPORT.REPORT_NAME.SEARCH_APPOINTMENT')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              label={t('REPORT.FILTER.BRANCH')}
              value={branchId}
              onChange={e => {
                setBranchId(e.target.value)
                setPage(1)
              }}
              options={branches.map(b => ({
                value: b.branchId,
                name: b.branchName || b.branchNameEn || ''
              }))}
              width="100%"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} xl={2}>
            <InputSecondNewRangePicker
              inputHeight={40}
              value={rangeDate}
              onchange={val => {
                if (!val || _.isEmpty(val)) setRangeDate(emptyRangePicker);
                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')]);
              }}
              label=""
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectCheckBoxInput
              label={t('USER_DOCTOR')}
              options={doctors.map(d => ({
                id: d.userId,
                name: d.fullname
              }))}
              selected={selectDoctors}
              onChange={(ids: any) => {
                setSelectDoctors(ids)
                setOpenFilterDoctors(false)
              }}
              fullWidth
              width="100%"
            />
          </Grid>
          <Grid item xs={12} md={1} xl={1} sx={{ pt: 0 }}>
            <MainButton
              title={t('INPUT.SEARCH')}
              type="primary"
              variant="contained"
              onClick={() => {
                setFetch(true);
                setLoading(true);
              }}
              style={{ width: '120px' }}
            />
          </Grid>
          <Grid item xs={12} md={11} xl={3} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <UseStyled.DropdownDocument>
              <Dropdown.Toggle id="dropdown-basic" className="ml-auto">
                {t('REGISTER.BUTTON.PRINT_DOCUMENT')}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={onDownload}>{t('Excel')}</Dropdown.Item>
              </Dropdown.Menu>
            </UseStyled.DropdownDocument>
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, px: { xs: 1, xl: 3 }, pb: 4 }}>
          <AppointmentList
            filterTable={getAllFilter()}
            page={page}
            pageLimit={pageLimit}
            rowCount={rowCount}
            sortBy={sortBy}
            sortType={sortType}
            data={appointments}
            dataExport={exportAppointments}
            date={date}
            onRequestSort={onRequestSort}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleChangePage={handleChangePage}
            componentRef={componentRef}
          />
        </Box>
      </Box>

      <Loading show={loading} type="fullLoading" />
    </Box>
  );
}